import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { buildFileUrl } from '../utils/fileUrl';

class PDFService {
  /**
   * Helper unificado de quebra de página segura para A4
   * Garante que se o conteúdo extrapolar a margem inferior (deixando 25mm livres para rodapé),
   * uma nova página é criada automaticamente com reset da margem superior.
   */
  checkPageBreak(pdf, currentY, neededHeight = 20, margin = 20, pageHeight = 297) {
    if (currentY + neededHeight > pageHeight - 25) {
      pdf.addPage();
      return margin;
    }
    return currentY;
  }

  /**
   * Gera PDF do levantamento
   * @param {Object} survey - Dados do levantamento
   * @param {Object} user - Dados do usuário logado
   */
  async generateSurveyPDF(survey, user) {
    try {
      console.log('📄 Iniciando geração de PDF para:', survey.survey_code);
      
      // Criar PDF em formato A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;

      // Configurar fonte
      pdf.setFont('helvetica');

      // === CABEÇALHO ===
      currentY = this.addHeader(pdf, survey, pageWidth, margin, currentY);

      // === DADOS DO CLIENTE ===
      currentY = this.addClientData(pdf, survey, pageWidth, margin, currentY);

      // === DADOS DO LEVANTAMENTO ===
      currentY = this.addSurveyData(pdf, survey, pageWidth, margin, currentY);

      // === INFORMAÇÕES TÉCNICAS ===
      currentY = this.addTechnicalInfo(pdf, survey, pageWidth, margin, currentY, pageHeight);

      // === INFORMAÇÕES COMERCIAIS (apenas para GERENCIA) ===
      if (user.role === 'GERENCIA' && (survey.complexity_level || survey.commercial_notes)) {
        currentY = this.addCommercialInfo(pdf, survey, pageWidth, margin, currentY, pageHeight);
      }

      // === AMBIENTES E MÓVEIS ===
      if (survey.rooms && survey.rooms.length > 0) {
        currentY = await this.addRoomsAndFurniture(pdf, survey, pageWidth, margin, currentY, pageHeight);
      }

      // === LISTA DE ARQUIVOS ===
      if (survey.files && survey.files.length > 0) {
        currentY = this.addFilesList(pdf, survey, pageWidth, margin, currentY, pageHeight);
      }

      // === IMAGENS ===
      if (survey.files && survey.files.length > 0) {
        await this.addImages(pdf, survey, pageWidth, margin, pageHeight);
      }

      // === CROQUI 2D (imagem do sketch) ===
      if (survey.sketch_snapshot_image) {
        await this.addSketchImage(pdf, survey, pageWidth, margin, pageHeight);
      }

      // === RODAPÉ ===
      this.addFooter(pdf, pageWidth, pageHeight, margin);

      // Salvar PDF
      const fileName = `Levantamento_${survey.survey_code}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      console.log('✅ PDF gerado com sucesso:', fileName);
      return { success: true, fileName };

    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      throw new Error('Erro ao gerar PDF: ' + error.message);
    }
  }

  /**
   * Adiciona cabeçalho do documento
   */
  addHeader(pdf, survey, pageWidth, margin, currentY) {
    // Título principal
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('LEVANTAMENTO DE CLIENTE', pageWidth / 2, currentY, { align: 'center' });
    currentY += 10;

    // Código do levantamento
    pdf.setFontSize(16);
    pdf.setTextColor(0, 102, 204); // Azul
    pdf.text(survey.survey_code || 'N/A', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;

    // Linha separadora
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    pdf.setTextColor(0, 0, 0); // Voltar para preto
    return currentY;
  }

  /**
   * Adiciona dados do cliente
   */
  addClientData(pdf, survey, pageWidth, margin, currentY) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DADOS DO CLIENTE', margin, currentY);
    currentY += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    const clientData = [
      ['Nome:', survey.client_name || 'N/A'],
      ['Telefone:', survey.client_phone || 'N/A'],
      ['Email:', survey.client_email || 'N/A'],
      ['Endereço:', survey.client_address || 'N/A']
    ];

    clientData.forEach(([label, value]) => {
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, margin, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(value, margin + 25, currentY);
      currentY += 6;
    });

    currentY += 5;
    return currentY;
  }

  /**
   * Adiciona dados do levantamento
   */
  addSurveyData(pdf, survey, pageWidth, margin, currentY) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DADOS DO LEVANTAMENTO', margin, currentY);
    currentY += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    const surveyData = [
      ['Data do Levantamento:', survey.survey_date ? survey.survey_date.split('T')[0].split('-').reverse().join('/') : 'N/A'],
      ['Responsável:', survey.surveyor_name || 'N/A'],
      ['Prazo do Projeto:', survey.project_deadline ? survey.project_deadline.split('T')[0].split('-').reverse().join('/') : 'N/A'],
      ['Status:', survey.status || 'N/A']
    ];

    surveyData.forEach(([label, value]) => {
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, margin, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(value, margin + 40, currentY);
      currentY += 6;
    });

    currentY += 5;
    return currentY;
  }

  /**
   * Adiciona informações técnicas
   */
  addTechnicalInfo(pdf, survey, pageWidth, margin, currentY, pageHeight) {
    // Verificar se precisa de nova página
    if (currentY > pageHeight - 60) {
      pdf.addPage();
      currentY = margin;
    }

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('INFORMAÇÕES TÉCNICAS', margin, currentY);
    currentY += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Observações técnicas
    if (survey.technical_observations) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Observações Técnicas:', margin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal');
      currentY = this.addWrappedText(pdf, survey.technical_observations, margin + 5, currentY, pageWidth - margin - 10);
      currentY += 5;
    }

    // Informações de encanamento
    if (survey.plumbing_info) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Informações de Encanamento/Instalações:', margin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal');
      currentY = this.addWrappedText(pdf, survey.plumbing_info, margin + 5, currentY, pageWidth - margin - 10);
      currentY += 5;
    }

    // Descrição do ambiente
    if (survey.environment_description) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Descrição do Ambiente:', margin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal');
      currentY = this.addWrappedText(pdf, survey.environment_description, margin + 5, currentY, pageWidth - margin - 10);
      currentY += 5;
    }

    // Informações do Projeto 3D (se existir)
    if (survey.project_3d_deadline || survey.project_3d_status || survey.project_3d_created_at) {
      if (currentY > pageHeight - 50) {
        pdf.addPage();
        currentY = margin;
      }

      currentY += 5;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(128, 0, 128); // Roxo
      pdf.text('PROJETO 3D', margin, currentY);
      currentY += 8;
      pdf.setTextColor(0, 0, 0); // Voltar para preto

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');

      if (survey.project_3d_deadline) {
        const deadlineDate = new Date(survey.project_3d_deadline);
        const isOverdue = deadlineDate < new Date();
        pdf.setFont('helvetica', 'bold');
        pdf.text('Prazo de Entrega:', margin, currentY);
        pdf.setFont('helvetica', 'normal');
        if (isOverdue) {
          pdf.setTextColor(220, 0, 0); // Vermelho para atrasado
        }
        pdf.text(deadlineDate.toLocaleString('pt-BR'), margin + 35, currentY);
        pdf.setTextColor(0, 0, 0);
        currentY += 8;
      }

      if (survey.project_3d_status) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Status:', margin, currentY);
        pdf.setFont('helvetica', 'normal');
        const statusLabels = {
          'PENDING': 'Pendente',
          'IN_PROGRESS': 'Em Andamento',
          'COMPLETED': 'Concluído',
          'REVIEW': 'Em Revisão',
          'APPROVED': 'Aprovado'
        };
        pdf.text(statusLabels[survey.project_3d_status] || survey.project_3d_status, margin + 35, currentY);
        currentY += 8;
      }

      if (survey.project_3d_created_at) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Data de Início:', margin, currentY);
        pdf.setFont('helvetica', 'normal');
        const startDate = new Date(survey.project_3d_created_at);
        pdf.text(startDate.toLocaleString('pt-BR'), margin + 35, currentY);
        currentY += 8;
      }
    }

    currentY += 5;
    return currentY;
  }

  /**
   * Adiciona informações comerciais (apenas GERENCIA)
   */
  addCommercialInfo(pdf, survey, pageWidth, margin, currentY, pageHeight) {
    // Verificar se precisa de nova página
    if (currentY > pageHeight - 40) {
      pdf.addPage();
      currentY = margin;
    }

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(204, 0, 0); // Vermelho para indicar confidencial
    pdf.text('INFORMAÇÕES COMERCIAIS (CONFIDENCIAL)', margin, currentY);
    currentY += 8;

    pdf.setTextColor(0, 0, 0); // Voltar para preto
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Nível de complexidade
    if (survey.complexity_level) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Nível de Complexidade:', margin, currentY);
      pdf.setFont('helvetica', 'normal');
      const complexityText = `${survey.complexity_level}/5 ${'★'.repeat(survey.complexity_level)}${'☆'.repeat(5 - survey.complexity_level)}`;
      pdf.text(complexityText, margin + 40, currentY);
      currentY += 8;
    }

    // Observações comerciais
    if (survey.commercial_notes) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Observações Comerciais:', margin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal');
      currentY = this.addWrappedText(pdf, survey.commercial_notes, margin + 5, currentY, pageWidth - margin - 10);
      currentY += 5;
    }

    currentY += 5;
    return currentY;
  }

  /**
   * Adiciona ambientes e móveis
   */
  async addRoomsAndFurniture(pdf, survey, pageWidth, margin, currentY, pageHeight) {
    currentY = this.checkPageBreak(pdf, currentY, 20, margin, pageHeight);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AMBIENTES E MÓVEIS', margin, currentY);
    currentY += 8;

    pdf.setFontSize(10);

    for (const room of survey.rooms) {
      const index = survey.rooms.indexOf(room);
      currentY = this.checkPageBreak(pdf, currentY, 35, margin, pageHeight);

      pdf.setFont('helvetica', 'bold');
      const roomDisplayName = room.environment_name || room.room_name || 'Ambiente sem nome';
      pdf.text(`${index + 1}. ${roomDisplayName}`, margin, currentY);
      currentY += 5;

      pdf.setFont('helvetica', 'normal');
      if (room.room_type) {
        currentY = this.checkPageBreak(pdf, currentY, 8, margin, pageHeight);
        pdf.text(`Tipo: ${room.room_type}`, margin + 5, currentY);
        currentY += 5;
      }
      if (room.dimensions) {
        currentY = this.checkPageBreak(pdf, currentY, 8, margin, pageHeight);
        pdf.text(`Dimensões: ${room.dimensions}`, margin + 5, currentY);
        currentY += 5;
      }
      if (room.observations) {
        currentY = this.checkPageBreak(pdf, currentY, 12, margin, pageHeight);
        pdf.text('Observações:', margin + 5, currentY);
        currentY += 4;
        currentY = this.addWrappedText(pdf, room.observations, margin + 10, currentY, pageWidth - margin - 15);
      }

      // Móveis do ambiente
      if (survey.furniture_items) {
        const roomFurniture = survey.furniture_items.filter(item => item.room_id === room.id);
        if (roomFurniture.length > 0) {
          currentY = this.checkPageBreak(pdf, currentY, 15, margin, pageHeight);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Móveis:', margin + 5, currentY);
          currentY += 5;

          roomFurniture.forEach(furniture => {
            currentY = this.checkPageBreak(pdf, currentY, 12, margin, pageHeight);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`• ${furniture.furniture_type}`, margin + 10, currentY);
            currentY += 4;
            if (furniture.intended_use) {
              pdf.text(`  Uso: ${furniture.intended_use}`, margin + 12, currentY);
              currentY += 4;
            }
            if (furniture.required_measurements) {
              pdf.text(`  Medidas: ${furniture.required_measurements}`, margin + 12, currentY);
              currentY += 4;
            }
          });
        }
      }

      // Croqui do ambiente
      if (room.sketch_snapshot_image) {
        const sketchImgUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${room.sketch_snapshot_image}`;
        const sketchImg = new Image();
        sketchImg.crossOrigin = 'anonymous';
        
        try {
          await new Promise((resolve, reject) => {
            sketchImg.onload = async () => {
              try {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = sketchImg.naturalWidth;
                canvas.height = sketchImg.naturalHeight;
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(sketchImg, 0, 0, sketchImg.naturalWidth, sketchImg.naturalHeight);
                const dataUrl = canvas.toDataURL('image/png', 1);
                
                const thumbWidth = 50;
                const thumbHeight = thumbWidth * (sketchImg.naturalHeight / sketchImg.naturalWidth);
                const maxThumbHeight = 40;
                let finalH = thumbHeight;
                let finalW = thumbWidth;
                if (finalH > maxThumbHeight) {
                  finalH = maxThumbHeight;
                  finalW = finalH * (sketchImg.naturalWidth / sketchImg.naturalHeight);
                }
                
                currentY += 3;
                pdf.addImage(dataUrl, 'PNG', margin + 5, currentY, finalW, finalH);
                currentY += finalH + 5;
              } catch (err) {
                console.warn('Erro ao adicionar croqui do ambiente:', err);
              }
              resolve();
            };
            sketchImg.onerror = () => {
              console.warn('Erro ao carregar croqui do ambiente:', sketchImgUrl);
              resolve();
            };
            sketchImg.src = sketchImgUrl;
          });
        } catch (err) {
          console.warn('Erro ao processar croqui do ambiente:', err);
        }
      }

      currentY += 5;
    }

    return currentY;
  }

  /**
   * Adiciona lista de arquivos
   */
  addFilesList(pdf, survey, pageWidth, margin, currentY, pageHeight) {
    // Verificar se precisa de nova página
    if (currentY > pageHeight - 40) {
      pdf.addPage();
      currentY = margin;
    }

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ARQUIVOS ANEXADOS', margin, currentY);
    currentY += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');

    const photos = survey.files.filter(file => file.file_type === 'PHOTO');
    const documents = survey.files.filter(file => file.file_type === 'DOCUMENT');

    if (photos.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Fotos:', margin, currentY);
      currentY += 5;

      photos.forEach((file, index) => {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${index + 1}. ${file.original_name}`, margin + 5, currentY);
        currentY += 4;
      });
      currentY += 3;
    }

    if (documents.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Documentos:', margin, currentY);
      currentY += 5;

      documents.forEach((file, index) => {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${index + 1}. ${file.original_name}`, margin + 5, currentY);
        currentY += 4;
      });
      currentY += 3;
    }

    return currentY;
  }

  /**
   * Adiciona imagens ao PDF
   */
  async addImages(pdf, survey, pageWidth, margin, pageHeight) {
    const photos = survey.files.filter(file => file.file_type === 'PHOTO');
    
    if (photos.length === 0) return;

    // Nova página para imagens
    pdf.addPage();
    let currentY = margin;

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('REGISTROS FOTOGRÁFICOS', margin, currentY);
    currentY += 15;

    const imagesPerRow = 2;
    const imageWidth = (pageWidth - margin * 2 - 10) / imagesPerRow;
    const imageHeight = imageWidth * 0.75; // Proporção 4:3

    // Calcular resolução alvo em pixels baseada em DPI
    const TARGET_DPI = 300; // aumente para 300 se quiser qualidade máxima (gera PDFs maiores)
    const mmToInch = (mm) => mm / 25.4;
    const targetPxWidth = Math.round(mmToInch(imageWidth) * TARGET_DPI);
    const targetPxHeight = Math.round(mmToInch(imageHeight) * TARGET_DPI);

    for (let i = 0; i < photos.length; i++) {
      const file = photos[i];
      const col = i % imagesPerRow;
      const row = Math.floor(i / imagesPerRow);

      // Verificar se precisa de nova página
      if (currentY + imageHeight + 20 > pageHeight - margin) {
        pdf.addPage();
        currentY = margin;
      }

      const x = margin + col * (imageWidth + 10);
      const y = currentY + row * (imageHeight + 25);

      try {
        // Carregar imagem
        const imageUrl = buildFileUrl(file.file_path);
        
        // Criar elemento img temporário para carregar a imagem
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              // Criar canvas temporário com DPI alvo
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');

              canvas.width = targetPxWidth;
              canvas.height = targetPxHeight;

              // Alta qualidade de interpolação
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';

              // Ajuste para manter proporção original dentro da área alvo
              const scale = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
              const drawW = Math.round(img.naturalWidth * scale);
              const drawH = Math.round(img.naturalHeight * scale);
              const offsetX = Math.floor((canvas.width - drawW) / 2);
              const offsetY = Math.floor((canvas.height - drawH) / 2);

              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, offsetX, offsetY, drawW, drawH);

              // Converter para base64 com qualidade alta
              const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
              
              // Adicionar ao PDF
              pdf.addImage(dataUrl, 'JPEG', x, y, imageWidth, imageHeight);
              
              // Adicionar nome da imagem
              pdf.setFontSize(8);
              pdf.setFont('helvetica', 'normal');
              const fileName = file.original_name.length > 30 
                ? file.original_name.substring(0, 27) + '...' 
                : file.original_name;
              pdf.text(fileName, x, y + imageHeight + 5);
              
              resolve();
            } catch (error) {
              console.warn('Erro ao processar imagem:', file.original_name, error);
              resolve(); // Continuar mesmo com erro
            }
          };
          
          img.onerror = () => {
            console.warn('Erro ao carregar imagem:', imageUrl);
            resolve(); // Continuar mesmo com erro
          };
          
          img.src = imageUrl;
        });

      } catch (error) {
        console.warn('Erro ao adicionar imagem ao PDF:', file.original_name, error);
      }

      // Atualizar currentY após cada linha
      if (col === imagesPerRow - 1 || i === photos.length - 1) {
        currentY = y + imageHeight + 15;
      }
    }
  }

  /**
   * Adiciona imagem do croqui 2D ao PDF
   */
  async addSketchImage(pdf, survey, pageWidth, margin, pageHeight) {
    try {
      const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${survey.sketch_snapshot_image}`;

      // Nova página para o croqui
      pdf.addPage();
      let currentY = margin;

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('CROQUI 2D DO AMBIENTE', margin, currentY);
      currentY += 10;

      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - currentY - margin;

      // Carregar imagem
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve) => {
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Usar a resolução original da imagem para máxima nitidez
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

            const dataUrl = canvas.toDataURL('image/png', 1);

            // Calcular tamanho em mm mantendo proporção, cabendo na área disponível
            const imgRatio = img.naturalWidth / img.naturalHeight;
            let drawW = availableWidth;
            let drawH = drawW / imgRatio;
            if (drawH > availableHeight) {
              drawH = availableHeight;
              drawW = drawH * imgRatio;
            }

            const x = margin + (availableWidth - drawW) / 2;
            const y = currentY + (availableHeight - drawH) / 2;

            pdf.addImage(dataUrl, 'PNG', x, y, drawW, drawH);
          } catch (err) {
            console.warn('Erro ao processar imagem do croqui:', err);
          }
          resolve();
        };

        img.onerror = () => {
          console.warn('Erro ao carregar imagem do croqui:', imageUrl);
          resolve();
        };

        img.src = imageUrl;
      });
    } catch (error) {
      console.warn('Erro ao adicionar croqui 2D ao PDF:', error);
    }
  }

  /**
   * Adiciona rodape
   */
  addFooter(pdf, pageWidth, pageHeight, margin) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);

      // Linha separadora
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

      // Texto do rodape
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);

      const footerText = `Sistema Santiago - Levantamento de Cliente - Gerado em ${new Date().toLocaleDateString('pt-BR')} as ${new Date().toLocaleTimeString('pt-BR')}`;
      pdf.text(footerText, margin, pageHeight - 10);

      // Numero da pagina
      pdf.text(`Pagina ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
  }

  /**
   * Adiciona texto com quebra de linha
   */
  addWrappedText(pdf, text, x, y, maxWidth) {
    const lines = pdf.splitTextToSize(text, maxWidth);
    pdf.text(lines, x, y);
    return y + (lines.length * 4);
  }

  /**
   * Gera PDF do holerite COMPLETO e DETALHADO
   * @param {Object} payslip - Dados do holerite
   */
  async generatePayslipPDF(payslip) {
    try {
      console.log('📄 Gerando PDF do holerite para:', payslip.full_name);
      console.log('📄 Dados do payslip:', JSON.stringify(payslip, null, 2));

      // Criar PDF em formato A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;

      // Configurar fonte
      pdf.setFont('helvetica');

      // === CABECALHO COM LOGO E TITULO ===
      currentY = await this.addPayslipHeader(pdf, payslip, pageWidth, margin, currentY);

      // === DADOS DO FUNCIONARIO ===
      currentY = this.addPayslipEmployeeData(pdf, payslip, pageWidth, margin, currentY);

      // === REMUNERACAO (VENCIMENTOS) ===
      currentY = this.addPayslipEarnings(pdf, payslip, pageWidth, margin, currentY);

      // === DESCONTOS ===
      currentY = this.addPayslipDeductions(pdf, payslip, pageWidth, margin, currentY);

      // === INFORMACOES DE APROVACAO ===
      if (payslip.status === 'APPROVED' && payslip.approvals && payslip.approvals.length > 0) {
        currentY = this.addPayslipApprovalInfo(pdf, payslip, pageWidth, margin, currentY);
      }

      // === RODAPE ===
      this.addPayslipFooter(pdf, pageWidth, pageHeight, margin);

      // Salvar PDF
      const monthNames = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const monthName = monthNames[payslip.ref_month - 1] || payslip.ref_month;
      const fileName = `Holerite_${payslip.full_name.replace(/\s+/g, '_')}_${monthName}_${payslip.ref_year}.pdf`;
      pdf.save(fileName);

      console.log('✅ PDF do holerite gerado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao gerar PDF do holerite:', error);
      throw error;
    }
  }

  /**
   * Adiciona cabeçalho do holerite com logo e informações da empresa
   */
  async addPayslipHeader(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    // Dados da empresa (vem do payslip.company)
    const company = payslip.company || {};
    const companyName = company.name || 'SANTIAGO PLANEJADOS LTDA';
    const companyCnpj = company.cnpj || '44.906.859/0001-30';
    const companyAddress = company.address || 'Rua Francisco Teixeira Ervilha, 50';
    const companyNeighborhood = company.neighborhood || 'Bairro Loteamento Ervilha';
    const companyCity = company.city || 'Inhapim/MG';

    // Nome da empresa + RECIBO DE PAGAMENTO na mesma linha
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(companyName, margin, currentY + 4);

    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(73, 148, 61);
    pdf.text('RECIBO DE PAGAMENTO', pageWidth - margin, currentY + 4, { align: 'right' });
    currentY += 7;

    // CNPJ
    pdf.setFontSize(5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(`CNPJ: ${companyCnpj}`, margin, currentY);
    currentY += 4;

    // Endereço
    pdf.setFontSize(4);
    pdf.text(`Endereco: ${companyAddress}`, margin, currentY);
    currentY += 4;
    pdf.text(`${companyNeighborhood} - ${companyCity}`, margin, currentY);
    currentY += 6;

    // Linha separadora
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 6;

    // Faixa verde com HOLERITE DE PAGAMENTO
    pdf.setFillColor(73, 148, 61);
    pdf.rect(margin, currentY, pageWidth - 2 * margin, 6, 'F');
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);

    const monthNames = [
      'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    pdf.text('HOLERITE DE PAGAMENTO', margin + 2, currentY + 4);
    pdf.text(`${monthNames[payslip.ref_month - 1]} / ${payslip.ref_year}`, pageWidth - margin - 2, currentY + 4, { align: 'right' });

    currentY += 18;
    pdf.setTextColor(0, 0, 0);

    return currentY;
  }

  /**
   * Adiciona dados do funcionario
   */
  addPayslipEmployeeData(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('DADOS DO FUNCIONARIO', margin, currentY);
    currentY += 5;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');

    // Dados em duas colunas
    const col2 = pageWidth / 2 + 10;

    pdf.text(`Nome: `, margin, currentY);
    const nameOffset = pdf.getTextWidth('Nome: ');
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${payslip.full_name}`, margin + nameOffset, currentY);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Cargo: ${payslip.role || 'N/A'}`, col2, currentY);
    currentY += 3;

    pdf.text(`E-mail: ${payslip.email || 'N/A'}`, margin, currentY);
    pdf.text(`Modalidade: ${payslip.salary_modality === 'MONTHLY' ? 'Mensalista' : 'Horista'}`, col2, currentY);
    currentY += 3;

    if (payslip.admission_date) {
      const admDate = payslip.admission_date.includes('T')
        ? payslip.admission_date.split('T')[0].split('-').reverse().join('/')
        : payslip.admission_date;
      pdf.text(`Admissao: ${admDate}`, margin, currentY);
    }

    currentY += 3;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 3;

    return currentY;
  }

  /**
   * Adiciona secao de VENCIMENTOS/REMUNERACAO detalhada
   */
  addPayslipEarnings(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    // Cabecalho da secao
    pdf.setFillColor(73, 148, 61); // Verde empresa
    pdf.rect(margin, currentY, pageWidth - 2 * margin, 6, 'F');
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('REMUNERAÇÃO', margin + 2, currentY + 4);
    currentY += 12;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(8);

    // Calcular valores
    const monthlySalary = Number(payslip.monthly_base_salary || 0);
    const overtimeWeekday = Number(payslip.overtime_salary_weekday || 0);
    const overtimeWeekend = Number(payslip.overtime_salary_weekend || 0);
    const inssDeduction = Number(payslip.inss_deduction || 0);
    const inssPercentage = payslip.inss_percentage || 0;
    const absenceBaseDeduction = Number(payslip.absence_base_deduction || 0);
    const advanceDeduction = Number(payslip.advance_deduction || 0);
    const diverseExpensesTotal = (payslip.diverse_expenses && payslip.diverse_expenses.length > 0)
      ? payslip.diverse_expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0)
      : 0;

    const extraAdvancesTotal = (payslip.extra_advances && payslip.extra_advances.length > 0)
      ? payslip.extra_advances.reduce((sum, a) => sum + Number(a.amount || 0), 0)
      : Number(payslip.extra_advances_total || 0);
    const holidayBonus = Number(payslip.holiday_bonus_amount || 0);
    const extraMonthly = Number(payslip.extra_monthly_amount || 0);
    const totalAdicionais = holidayBonus + extraMonthly + extraAdvancesTotal;

    // Salario base ou horas trabalhadas
    if (payslip.salary_modality === 'MONTHLY') {
      const hasWorkCard = payslip.has_work_card === true;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text(hasWorkCard ? 'Salario Mensal (CLT)' : 'Salario Mensal', margin, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        monthlySalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 5;

      // Dias no mes / referencia
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Dias no Mes / Referencia: 30 dias', margin, currentY);
      currentY += 5;

      // Deducao de ausencias (se houver)
      if (absenceBaseDeduction > 0) {
        pdf.setFontSize(7);
        pdf.setTextColor(200, 100, 0);
        pdf.text(`(-) Deducao Ausencias`, margin, currentY);
        pdf.text(
          `- ${absenceBaseDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
          pageWidth - margin,
          currentY,
          { align: 'right' }
        );
        currentY += 4;

        // Detalhes inline das ausencias
        if (payslip.absence_details && payslip.absence_details.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7.5);
          pdf.setTextColor(80, 80, 80);
          payslip.absence_details.forEach((detail) => {
            const cleanDate = String(detail.date || '').split('T')[0];
            const parts = cleanDate.split('-');
            const dateStr = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : cleanDate;
            const typeStr = detail.type_description || 'Dia Inteiro';
            pdf.text(`${dateStr} - ${typeStr}`, margin + 3, currentY);
            currentY += 3.5;
          });
          pdf.setTextColor(0, 0, 0);
          currentY += 1;
        }

        // Base INSS/FGTS (salario - deducao) - apenas para quem tem carteira
        if (hasWorkCard) {
          const inssBase = monthlySalary - absenceBaseDeduction;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(0, 0, 0);
          pdf.text(`Base INSS/FGTS`, margin, currentY);
          pdf.text(
            inssBase.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 5;
          pdf.setTextColor(0, 0, 0);
        }
      }

      // INSS (subtraindo do salario) - apenas para quem tem carteira
      if (inssDeduction > 0 && hasWorkCard) {
        currentY += 2;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(200, 0, 0);
        pdf.text(`INSS (${inssPercentage}%)`, margin, currentY);
        pdf.text(
          `- ${inssDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
          pageWidth - margin,
          currentY,
          { align: 'right' }
        );
        currentY += 5;
        pdf.setTextColor(0, 0, 0);
      }

      // Liquido apos INSS (salario - ausencia - inss)
      const liquidoAposInss = hasWorkCard
        ? monthlySalary - absenceBaseDeduction - inssDeduction
        : monthlySalary - absenceBaseDeduction;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 100, 0);
      pdf.text('Liquido', margin, currentY);
      pdf.text(
        liquidoAposInss.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 6;
      pdf.setTextColor(0, 0, 0);

      // Horas Extras (soma ao liquido)
      if (overtimeWeekday > 0 || overtimeWeekend > 0) {
        currentY += 1;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 102, 204);
        pdf.text('Horas Extras', margin, currentY);
        pdf.setTextColor(0, 0, 0);
        currentY += 4;

        if (overtimeWeekday > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          const decimalHours = Number(payslip.overtime_hours_weekday || 0);
          const hoursWeekday = Math.floor(decimalHours);
          const minutesWeekday = Math.round((decimalHours - hoursWeekday) * 60);
          pdf.text(`Quantidade de horas extras em Dias Uteis: ${hoursWeekday}:${String(minutesWeekday).padStart(2, '0')}h`, margin + 3, currentY);
          pdf.text(
            `+ ${overtimeWeekday.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;
        }
        if (overtimeWeekend > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          const decimalHours = Number(payslip.overtime_hours_weekend || 0);
          const hoursWeekend = Math.floor(decimalHours);
          const minutesWeekend = Math.round((decimalHours - hoursWeekend) * 60);
          pdf.text(`Quantidade de horas extras em Fins de Semana: ${hoursWeekend}:${String(minutesWeekend).padStart(2, '0')}h`, margin + 3, currentY);
          pdf.text(
            `+ ${overtimeWeekend.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;
        }

        // Detalhamento das horas extras (apenas informativo, uma linha)
        // Verificar modo manual: se overtime_manual_rate_weekday > 0, usar esse valor
        if (payslip.overtime_details && payslip.overtime_details.length > 0) {
          const firstOt = payslip.overtime_details[0];
          const multiplier = firstOt.multiplier || 2;
          const monthlySalary = Number(payslip.monthly_base_salary) || 0;

          // Verificar se usa modo manual
          const useManualWeekday = payslip.overtime_manual_rate_weekday > 0;
          const useManualWeekend = payslip.overtime_manual_rate_weekend > 0;
          const manualRateWeekday = Number(payslip.overtime_manual_rate_weekday || 0);
          const manualRateWeekend = Number(payslip.overtime_manual_rate_weekend || 0);

          // Calcular hourly rate base
          const baseHourlyRate = monthlySalary > 0 ? Math.round((monthlySalary / 220) * 100) / 100 : 0;

          // Usar valor manual se disponível, caso contrário usar cálculo automático
          const displayRateWeekday = useManualWeekday ? manualRateWeekday : baseHourlyRate;
          const displayRateWeekend = useManualWeekend ? manualRateWeekend : baseHourlyRate;
          currentY += 1;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8);
          pdf.setTextColor(60, 60, 60);
          pdf.text(
            `Valor Hora: R$ ${displayRateWeekday.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | Mult: ${multiplier}x`,
            margin + 4,
            currentY
          );
          currentY += 4;
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
        }
      }

      // Adicionais (soma ao liquido)
      if (totalAdicionais > 0) {
        currentY += 1;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 102, 204);
        pdf.text('Adicionais', margin, currentY);
        pdf.setTextColor(0, 0, 0);
        currentY += 4;

        if (holidayBonus > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.text('Adicional Feriado:', margin + 3, currentY);
          pdf.text(
            `+ ${holidayBonus.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;
        }

        if (extraMonthly > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.text('Adicional Mensal:', margin + 3, currentY);
          pdf.text(
            `+ ${extraMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;
        }

        if (extraAdvancesTotal > 0) {
          const countAdvances = (payslip.extra_advances && payslip.extra_advances.length > 0) ? payslip.extra_advances.length : 1;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.text(`Bonificação / Bônus (${countAdvances}):`, margin + 3, currentY);
          pdf.text(
            `+ ${extraAdvancesTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;

          // Detalhes dos bônus com fonte aumentada e boa legibilidade
          if (payslip.extra_advances && payslip.extra_advances.length > 0) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(60, 60, 60);
            payslip.extra_advances.forEach((advance) => {
              const desc = advance.description || 'Bônus';
              const amount = Number(advance.amount || 0);
              pdf.text(
                `  - ${desc}: + R$ ${amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                margin + 4,
                currentY
              );
              currentY += 4;
            });
            pdf.setTextColor(0, 0, 0);
          }
        }
      }

      // Sábados (valor fixo) - mover para antes do cálculo do líquido
      const fixedSaturdayAmount = Number(payslip.fixed_saturday_amount || 0);
      if (fixedSaturdayAmount > 0) {
        currentY += 2;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Sábados (valor fixo):', margin, currentY);
        pdf.text(
          fixedSaturdayAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          pageWidth - margin,
          currentY,
          { align: 'right' }
        );
        pdf.setFont('helvetica', 'normal');
        currentY += 4;

        // Listar sabados trabalhados com fonte aumentada e boa legibilidade
        if (payslip.worked_saturdays && payslip.worked_saturdays.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(60, 60, 60);
          const saturdaysText = payslip.worked_saturdays
            .map(sat => `${String(sat.day).padStart(2, '0')}/${String(sat.month).padStart(2, '0')}/${sat.year}`)
            .join(', ');
          pdf.text(`Sabados trabalhados: ${saturdaysText}`, margin + 3, currentY);
          pdf.setTextColor(0, 0, 0);
          currentY += 4;
        }
        currentY += 2;
      }

      // SALARIO LIQUIDO SEM DESCONTOS
      const salarioLiquidoSemDescontos = liquidoAposInss + overtimeWeekday + overtimeWeekend + fixedSaturdayAmount + totalAdicionais;
      currentY += 2;
      pdf.setDrawColor(73, 148, 61);
      pdf.setFillColor(232, 245, 223);
      pdf.rect(margin, currentY - 2, pageWidth - 2 * margin, 8, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(0, 100, 0);
      pdf.text('SALÁRIO LÍQUIDO SEM DESCONTOS', margin + 2, currentY + 4);
      pdf.text(
        salarioLiquidoSemDescontos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin - 2,
        currentY + 4,
        { align: 'right' }
      );
      currentY += 10;
      pdf.setTextColor(0, 0, 0);

      // Descontos (subtrair do SALARIO LIQUIDO SEM DESCONTOS)
      if (advanceDeduction > 0 || diverseExpensesTotal > 0) {
        currentY += 1;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(200, 0, 0);
        pdf.text('Descontos', margin, currentY);
        currentY += 4;

        if (advanceDeduction > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(200, 0, 0);
          pdf.text('Adiantamento Quinzenal', margin + 3, currentY);
          pdf.text(
            `- ${advanceDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;
        }

        if (diverseExpensesTotal > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(200, 0, 0);
          pdf.text(`Despesas Diversas (${payslip.diverse_expenses.length})`, margin + 3, currentY);
          pdf.text(
            `- ${diverseExpensesTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
            pageWidth - margin,
            currentY,
            { align: 'right' }
          );
          currentY += 4;

          // Detalhes das despesas diversas
          pdf.setFontSize(6);
          pdf.setTextColor(100, 100, 100);
          const textMaxWidth = pageWidth - margin - 6 - (margin + 6);
          payslip.diverse_expenses.forEach((expense) => {
            const type = expense.expense_type_name || 'Despesa';
            const desc = expense.description || '';
            const installment = expense.installment_number;
            const totalInstallments = expense.installments_count;
            const installmentInfo = installment && totalInstallments ? ` (${installment}/${totalInstallments})` : '';
            const fullText = `  - ${type}: ${desc}${installmentInfo}`;
            const lines = pdf.splitTextToSize(fullText, textMaxWidth);
            lines.forEach((line) => {
              pdf.text(line, margin + 6, currentY);
              currentY += 3;
            });
          });
          pdf.setFontSize(8);
        }
        pdf.setTextColor(0, 0, 0);
      }

      // SALARIO LIQUIDO FINAL
      const totalDescontos = advanceDeduction + diverseExpensesTotal;
      const salarioLiquidoFinal = salarioLiquidoSemDescontos - totalDescontos;
      currentY += 2;
      pdf.setDrawColor(73, 148, 61);
      pdf.setFillColor(73, 148, 61);
      pdf.rect(margin, currentY - 2, pageWidth - 2 * margin, 10, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text('SALÁRIO LÍQUIDO FINAL', margin + 2, currentY + 5);
      pdf.text(
        salarioLiquidoFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin - 2,
        currentY + 5,
        { align: 'right' }
      );
      currentY += 12;
      pdf.setTextColor(0, 0, 0);

      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 5;

      return currentY;
    }
  }

  /**
   * Adiciona secao de DESCONTOS detalhada
   */
  addPayslipDeductions(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY + 6; // Espaco antes do cabecalho

    // Encargos da Empresa - FGTS (Informativo, não descontado do funcionário)
    // Aparece para todo colaborador com carteira assinada (CLT)
    if (payslip.has_work_card === true && payslip.monthly_base_salary) {
      const fgtsBase = Number(payslip.inss_base || payslip.monthly_base_salary);
      const fgtsPercentage = payslip.fgts_percentage || 8;
      const fgtsValue = payslip.fgts_deduction !== undefined && Number(payslip.fgts_deduction) > 0
        ? Number(payslip.fgts_deduction)
        : fgtsBase * (fgtsPercentage / 100);

      currentY += 2;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(0, 100, 180);
      pdf.text('Encargos da Empresa', margin, currentY);
      currentY += 4;
      pdf.setFont('helvetica', 'normal');
      pdf.text(`fgts - empresa (${fgtsPercentage}%)`, margin, currentY);
      pdf.setTextColor(0, 100, 180);
      pdf.text(
        fgtsValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      pdf.setTextColor(0, 0, 0);
      currentY += 5;
    }

    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;

    return currentY;
  }

  /**
   * Calcula total de descontos
   */
  calculateTotalDeductions(payslip) {
    let total = 0;

    // INSS
    total += Number(payslip.inss_deduction || 0);

    // Adiantamento
    total += Number(payslip.advance_deduction || 0);

    // Despesas diversas
    if (payslip.diverse_expenses) {
      total += payslip.diverse_expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
    } else if (payslip.diverse_expenses_total) {
      total += Number(payslip.diverse_expenses_total || 0);
    }

    return total;
  }

  /**
   * Adiciona RESUMO FINAL
   */
  addPayslipSummary(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    const holidayBonus = Number(payslip.holiday_bonus_amount || 0);
    const extraMonthly = Number(payslip.extra_monthly_amount || 0);
    const totalGross =
       Number(payslip.monthly_base_salary || 0) +
       Number(payslip.overtime_salary_weekday || 0) +
       Number(payslip.overtime_salary_weekend || 0);
    const totalComAdicionais = totalGross + holidayBonus + extraMonthly;
    const totalDeductions = this.calculateTotalDeductions(payslip);
    const totalSalary = totalComAdicionais - totalDeductions;

    // Altura total compactada
    const hasAdicionais = holidayBonus > 0 || extraMonthly > 0;
    const boxHeight = hasAdicionais ? 38 : 32;

    // Fundo gradiente verde para todo o resumo
    pdf.setFillColor(73, 148, 61);
    pdf.rect(margin, currentY, pageWidth - 2 * margin, boxHeight, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(7);

    // Total Bruto (ja com adicionais)
    pdf.setFont('helvetica', 'normal');
    pdf.text('Total Bruto', margin + 3, currentY + 7);
    pdf.text(
      totalComAdicionais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      pageWidth - margin - 3,
      currentY + 7,
      { align: 'right' }
    );

    // Total Descontos (vermelho claro)
    pdf.setTextColor(255, 200, 200); // vermelho claro
    pdf.text('Total Descontos', margin + 3, currentY + 14);
    pdf.text(
      `- ${totalDeductions.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
      pageWidth - margin - 3,
      currentY + 14,
      { align: 'right' }
    );

    // Linha separadora - FULL WIDTH
    const separatorY = currentY + 22;
    const salaryY = currentY + 29;

    pdf.setDrawColor(255, 255, 255, 0.5);
    pdf.setLineWidth(0.3);
    pdf.line(margin, separatorY, pageWidth - margin, separatorY);

    // SALARIO LIQUIDO
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(255, 255, 255);
    pdf.text('SALARIO LIQUIDO', margin + 2, salaryY);
    pdf.text(
      totalSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      pageWidth - margin - 2,
      salaryY,
      { align: 'right' }
    );

    currentY += boxHeight + 3;
    pdf.setTextColor(0, 0, 0);

    return currentY;
  }

  /**
   * Adiciona seção de horas trabalhadas
   */
  addPayslipWorkedHours(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('HORAS TRABALHADAS', margin, currentY);
    currentY += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Dias úteis
    pdf.text(`Dias úteis: ${Number(payslip.regular_hours_weekday || 0).toFixed(2)} h`, margin, currentY);
    pdf.text(
      `Valor: ${Number(payslip.regular_salary_weekday || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
      pageWidth - margin,
      currentY,
      { align: 'right' }
    );
    currentY += 7;

    // Domingos
    if (Number(payslip.regular_hours_weekend || 0) > 0) {
      pdf.text(`Domingos: ${Number(payslip.regular_hours_weekend || 0).toFixed(2)} h`, margin, currentY);
      pdf.text(
        `Valor: ${Number(payslip.regular_salary_weekend || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 7;
    }

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona seção de horas extras
   */
  // Função auxiliar para formatar valores monetários com 3 casas decimais
  formatCurrency3Decimals(value) {
    const numValue = Number(value || 0);
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  addPayslipOvertimeHours(pdf, payslip, pageWidth, margin, startY, pageHeight) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('HORAS EXTRAS', margin, currentY);
    currentY += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    // Dias úteis
    if (Number(payslip.overtime_hours_weekday || 0) > 0) {
      pdf.text(`Tempo Trabalhado: ${Number(payslip.overtime_hours_weekday || 0).toFixed(2)} h`, margin, currentY);
      pdf.text(
        `Valor: ${Number(payslip.overtime_salary_weekday || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 7;
    }

    // Fins de semana (sábados e domingos)
    if (Number(payslip.overtime_hours_weekend || 0) > 0) {
      pdf.text(`Fins de semana: ${Number(payslip.overtime_hours_weekend || 0).toFixed(2)} h`, margin, currentY);
      pdf.text(
        `Valor: ${Number(payslip.overtime_salary_weekend || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 7;
    }

    // Detalhamento dia a dia
    if (payslip.overtime_details && payslip.overtime_details.length > 0) {
      currentY += 5;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Detalhamento por dia:', margin, currentY);
      currentY += 6;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');

      // Cabeçalho da tabela
      pdf.setFont('helvetica', 'bold');
      pdf.text('Data', margin, currentY);
      pdf.text('Dia', margin + 30, currentY);
      pdf.text('Tempo', margin + 60, currentY);
      pdf.text('Valor Hora', margin + 95, currentY);
      pdf.text('Mult.', margin + 135, currentY);
      pdf.text('Valor Total', pageWidth - margin, currentY, { align: 'right' });
      currentY += 5;

      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      pdf.setFont('helvetica', 'normal');

      // Linhas de detalhes
      payslip.overtime_details.forEach((detail) => {
        const formatTime = (minutes) => {
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          if (hours > 0 && mins > 0) {
            return `${hours}h ${mins}min`;
          } else if (hours > 0) {
            return `${hours}h`;
          } else {
            return `${mins}min`;
          }
        };

        const dateStr = `${String(detail.day).padStart(2, '0')}/${String(detail.month).padStart(2, '0')}/${detail.year}`;
        const timeStr = formatTime(detail.minutes);
        const multiplierStr = detail.multiplier === 1.0 ? '1x' : detail.multiplier === 1.5 ? '1.5x' : '2x';

        pdf.text(dateStr, margin, currentY);
        pdf.text(detail.dayName.substring(0, 3), margin + 30, currentY);
        pdf.text(timeStr, margin + 60, currentY);
        pdf.text(
          detail.hourlyRate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          margin + 95,
          currentY
        );
        pdf.text(multiplierStr, margin + 135, currentY);
        // Formatar valor com 3 casas decimais
        const valueFormatted = `R$ ${this.formatCurrency3Decimals(detail.value)}`;
        pdf.text(
          valueFormatted,
          pageWidth - margin,
          currentY,
          { align: 'right' }
        );
        currentY += 5;

        // Verificar se precisa de nova página
        if (currentY > pageHeight - 30) {
          pdf.addPage();
          currentY = margin;
        }
      });

      // Adicionar linha de total
      currentY += 3;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Calcular e exibir total
      const totalOvertime = payslip.overtime_details.reduce((sum, detail) => sum + Number(detail.value || 0), 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Total:', margin + 135, currentY);
      pdf.text(
        `R$ ${this.formatCurrency3Decimals(totalOvertime)}`,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 5;
    }

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona seção de divergências/faltas descontadas
   */
  addPayslipAbsences(pdf, payslip, pageWidth, margin, startY, pageHeight) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(220, 38, 38); // Vermelho
    pdf.text('DIVERGÊNCIAS/FALTAS DESCONTADAS', margin, currentY);
    currentY += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0); // Preto
    pdf.text('As seguintes divergências não justificadas foram descontadas do salário:', margin, currentY);
    currentY += 8;

    // Cabeçalho da tabela
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Data', margin, currentY);
    pdf.text('Tipo', margin + 30, currentY);
    pdf.text('Horas', margin + 80, currentY);
    pdf.text('Valor Descontado', pageWidth - margin, currentY, { align: 'right' });
    currentY += 5;

    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;

    // Detalhes das divergências
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    payslip.absence_details.forEach((detail) => {
      // Formatar data sem usar new Date() para evitar problema de fuso horário
      const dateStr = detail.date && typeof detail.date === 'string' && detail.date.includes('T')
        ? detail.date.split('T')[0].split('-').reverse().join('/')
        : new Date(detail.date).toLocaleDateString('pt-BR');
      const hoursStr = `${detail.hours.toFixed(2)}h`;
      const valueStr = `- R$ ${detail.value.toFixed(2).replace('.', ',')}`;

      pdf.text(dateStr, margin, currentY);
      pdf.text(detail.type_description, margin + 30, currentY);
      pdf.text(hoursStr, margin + 80, currentY);
      pdf.setTextColor(220, 38, 38); // Vermelho para valor
      pdf.text(
        valueStr,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      pdf.setTextColor(0, 0, 0); // Voltar para preto
      currentY += 5;

      // Verificar se precisa de nova página
      if (currentY > pageHeight - 30) {
        pdf.addPage();
        currentY = margin;
      }
    });

    // Adicionar linha de total
    currentY += 3;
    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 4;

    // Calcular e exibir total
    const totalAbsence = payslip.absence_details.reduce((sum, detail) => sum + Number(detail.value || 0), 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Total Descontado:', margin + 80, currentY);
    pdf.setTextColor(220, 38, 38); // Vermelho
    pdf.text(
      `- R$ ${totalAbsence.toFixed(2).replace('.', ',')}`,
      pageWidth - margin,
      currentY,
      { align: 'right' }
    );
    pdf.setTextColor(0, 0, 0); // Voltar para preto
    currentY += 5;

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona seção de vales extras descontados
   */
  addPayslipExtraAdvances(pdf, payslip, pageWidth, margin, startY, pageHeight) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(220, 38, 38); // Vermelho
    pdf.text('VALES EXTRAS DESCONTADOS', margin, currentY);
    currentY += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0); // Preto
    pdf.text('Os seguintes vales extras foram descontados do salário:', margin, currentY);
    currentY += 8;

    // Cabeçalho da tabela
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Data', margin, currentY);
    pdf.text('Descrição', margin + 40, currentY);
    pdf.text('Valor Descontado', pageWidth - margin, currentY, { align: 'right' });
    currentY += 5;

    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;

    // Detalhes dos vales
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    payslip.extra_advances.forEach((advance) => {
      // Formatar data sem usar new Date() para evitar problema de fuso horário
      const dateStr = advance.request_date && typeof advance.request_date === 'string' && advance.request_date.includes('T')
        ? advance.request_date.split('T')[0].split('-').reverse().join('/')
        : new Date(advance.request_date).toLocaleDateString('pt-BR');
      const description = advance.description || 'Vale extra';
      const valueStr = `- R$ ${Number(advance.amount).toFixed(2).replace('.', ',')}`;

      pdf.text(dateStr, margin, currentY);
      pdf.text(description.substring(0, 40), margin + 40, currentY);
      pdf.setTextColor(220, 38, 38); // Vermelho para valor
      pdf.text(
        valueStr,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      pdf.setTextColor(0, 0, 0); // Voltar para preto
      currentY += 5;

      // Verificar se precisa de nova página
      if (currentY > pageHeight - 30) {
        pdf.addPage();
        currentY = margin;
      }
    });

    // Adicionar linha de total
    currentY += 3;
    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 4;

    // Calcular e exibir total
    const totalExtraAdvances = payslip.extra_advances.reduce((sum, advance) => sum + Number(advance.amount || 0), 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Total Descontado:', margin + 40, currentY);
    pdf.setTextColor(220, 38, 38); // Vermelho
    pdf.text(
      `- R$ ${totalExtraAdvances.toFixed(2).replace('.', ',')}`,
      pageWidth - margin,
      currentY,
      { align: 'right' }
    );
    pdf.setTextColor(0, 0, 0); // Voltar para preto
    currentY += 5;

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona seção de despesas diversas (parcelas do mês)
   */
  addPayslipDiverseExpenses(pdf, payslip, pageWidth, margin, startY, pageHeight) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(220, 38, 38); // Vermelho
    pdf.text('DESPESAS DIVERSAS (GERÊNCIA)', margin, currentY);
    currentY += 8;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0); // Preto
    pdf.text(
      'As seguintes parcelas de despesas diversas foram descontadas neste mês:',
      margin,
      currentY
    );
    currentY += 8;

    // Cabeçalho da tabela
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('Tipo', margin, currentY);
    pdf.text('Descrição', margin + 40, currentY);
    pdf.text('Parcela', margin + 110, currentY);
    pdf.text('Valor', pageWidth - margin, currentY, { align: 'right' });
    currentY += 5;

    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 5;

    // Detalhes das despesas
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    payslip.diverse_expenses.forEach((inst) => {
      const typeName = inst.expense_type_name || 'Despesa';
      const description = inst.description || '';
      const parcelaLabel = `${inst.installment_number}/${inst.installments_count || '?'}`;
      const valueStr = `- R$ ${Number(inst.amount || 0).toFixed(2).replace('.', ',')}`;

      pdf.text(typeName.substring(0, 20), margin, currentY);
      pdf.text(description.substring(0, 60), margin + 40, currentY);
      pdf.text(parcelaLabel, margin + 110, currentY);
      pdf.setTextColor(220, 38, 38); // Vermelho para valor
      pdf.text(
        valueStr,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      pdf.setTextColor(0, 0, 0); // Voltar para preto
      currentY += 5;

      // Verificar se precisa de nova página
      if (currentY > pageHeight - 30) {
        pdf.addPage();
        currentY = margin;
      }
    });

    // Total de despesas diversas
    const totalDiverseExpenses = payslip.diverse_expenses.reduce(
      (sum, inst) => sum + Number(inst.amount || 0),
      0
    );

    currentY += 3;
    pdf.setDrawColor(220, 38, 38);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 4;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Total Descontado em Despesas Diversas:', margin + 40, currentY);
    pdf.setTextColor(220, 38, 38);
    pdf.text(
      `- R$ ${totalDiverseExpenses.toFixed(2).replace('.', ',')}`,
      pageWidth - margin,
      currentY,
      { align: 'right' }
    );
    pdf.setTextColor(0, 0, 0);
    currentY += 5;

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona seção de adicionais
   */
  addPayslipBonuses(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ADICIONAIS', margin, currentY);
    currentY += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');

    if (Number(payslip.holiday_bonus_amount || 0) > 0) {
      pdf.text('Adicional Feriado:', margin, currentY);
      pdf.text(
        Number(payslip.holiday_bonus_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 7;
    }

    if (Number(payslip.extra_monthly_amount || 0) > 0) {
      pdf.text('Adicional Mensal:', margin, currentY);
      pdf.text(
        Number(payslip.extra_monthly_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      currentY += 7;
    }

    // Desconto de adiantamento quinzenal
    if (Number(payslip.advance_deduction || 0) > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(200, 0, 0);
      pdf.text('Desconto - Adiantamento Quinzenal:', margin, currentY);
      pdf.text(
        `-${Number(payslip.advance_deduction || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
        pageWidth - margin,
        currentY,
        { align: 'right' }
      );
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      currentY += 7;
    }

    currentY += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 10;

    return currentY;
  }

  /**
   * Adiciona total
   */
  addPayslipTotal(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 150, 0);
    pdf.text('TOTAL A RECEBER:', margin, currentY);
    pdf.text(
      Number(payslip.total_salary || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      pageWidth - margin,
      currentY,
      { align: 'right' }
    );
    pdf.setTextColor(0, 0, 0);
    currentY += 15;

    return currentY;
  }

  /**
   * Adiciona informações de aprovação
   */
  addPayslipApprovalInfo(pdf, payslip, pageWidth, margin, startY) {
    let currentY = startY;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(100, 100, 100);

    // Formatar data e hora sem usar new Date() para evitar problema de fuso horário
    const approvedDateStr = payslip.approved_at && typeof payslip.approved_at === 'string' && payslip.approved_at.includes('T')
      ? (() => {
          const [datePart, timePart] = payslip.approved_at.split('T');
          const [year, month, day] = datePart.split('-');
          const [hours, minutes] = timePart.split(':');
          return `${day}/${month}/${year} às ${hours}:${minutes}`;
        })()
      : new Date(payslip.approved_at).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

    pdf.text(
      `Aprovado em ${approvedDateStr} por ${payslip.approver_name || 'N/A'}`,
      margin,
      currentY
    );
    currentY += 8;

    if (payslip.adjustment_notes) {
      pdf.text('Observações:', margin, currentY);
      currentY += 5;
      pdf.setFontSize(8);
      const notesLines = pdf.splitTextToSize(payslip.adjustment_notes, pageWidth - 2 * margin);
      pdf.text(notesLines, margin, currentY);
      currentY += notesLines.length * 4;
    }

    return currentY;
  }

  /**
   * Gera a VIA DE RECIBO DE PAGAMENTO COM TERMO DE QUITAÇÃO E CAMPO DE ASSINATURA
   * Diferencia com clareza Pagamento Único vs Pagamento Fracionado
   */
  async generatePayslipReceiptPDF(payslip) {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;

      pdf.setFont('helvetica');

      // === CABEÇALHO ===
      const company = payslip.company || {};
      const companyName = company.name || 'SANTIAGO PLANEJADOS LTDA';
      const companyCnpj = company.cnpj || '44.906.859/0001-30';
      const companyAddress = company.address || 'Rua Francisco Teixeira Ervilha, 50 - Inhapim/MG';

      // Logo / Título Empresa
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(73, 148, 61);
      pdf.text(companyName, margin, currentY);

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`CNPJ: ${companyCnpj} • ${companyAddress}`, margin, currentY + 5);

      currentY += 12;

      // Banner Título
      pdf.setFillColor(73, 148, 61);
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text('RECIBO DE PAGAMENTO E DECLARAÇÃO DE QUITAÇÃO', pageWidth / 2, currentY + 6.5, { align: 'center' });

      currentY += 16;
      pdf.setTextColor(0, 0, 0);

      // === DADOS DO COLABORADOR E COMPETÊNCIA ===
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const monthName = monthNames[payslip.ref_month - 1] || payslip.ref_month;

      pdf.setFillColor(245, 247, 250);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 24, 2, 2, 'F');
      pdf.setDrawColor(220, 225, 230);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 24, 2, 2, 'S');

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Colaborador:', margin + 4, currentY + 6);
      pdf.setFont('helvetica', 'normal');
      pdf.text(payslip.full_name || 'N/A', margin + 28, currentY + 6);

      pdf.setFont('helvetica', 'bold');
      pdf.text('Cargo/Função:', margin + 4, currentY + 13);
      pdf.setFont('helvetica', 'normal');
      pdf.text(payslip.role || payslip.position || 'Colaborador', margin + 28, currentY + 13);

      pdf.setFont('helvetica', 'bold');
      pdf.text('Competência:', pageWidth / 2 + 10, currentY + 6);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${monthName} / ${payslip.ref_year}`, pageWidth / 2 + 35, currentY + 6);

      pdf.setFont('helvetica', 'bold');
      pdf.text('Tipo:', pageWidth / 2 + 10, currentY + 13);
      pdf.setFont('helvetica', 'normal');
      pdf.text(payslip.payslip_type === 'PLR' ? 'PLR' : payslip.payslip_type === '13_FIRST_HALF' ? '13º (1ª Parcela)' : payslip.payslip_type === '13_SECOND_HALF' ? '13º (2ª Parcela)' : 'Folha Mensal', pageWidth / 2 + 35, currentY + 13);

      currentY += 28;

      // === COMPOSIÇÃO DOS VENCIMENTOS E DESCONTOS (DEMONSTRATIVO DO LÍQUIDO) ===
      const baseSalary = Number(payslip.regular_salary_weekday || payslip.monthly_base_salary || 0);
      const satAmount = Number(payslip.fixed_saturday_amount || 0);
      const otWeekday = Number(payslip.overtime_salary_weekday || 0);
      const otWeekend = Number(payslip.overtime_salary_weekend || 0);
      const extraMonthly = Number(payslip.extra_monthly_amount || 0);
      const holidayBonus = Number(payslip.holiday_bonus_amount || 0);
      const extraAdvances = Number(payslip.extra_advances_total || 0);

      const grossCalculated = baseSalary + satAmount + otWeekday + otWeekend + extraMonthly + holidayBonus + extraAdvances;

      const inssDeduction = Number(payslip.inss_deduction || 0);
      const advanceDeduction = Number(payslip.advance_deduction || 0);
      const diverseExpenses = Number(payslip.diverse_expenses_total || 0);
      const absenceDeduction = Number(payslip.absence_base_deduction || 0);

      const totalDeductions = inssDeduction + advanceDeduction + diverseExpenses + absenceDeduction;

      // Cálculo do salário líquido real
      let computedNet = grossCalculated - totalDeductions;
      if (computedNet <= 0 && Number(payslip.total_salary || 0) > 0) {
        computedNet = Number(payslip.total_salary);
      }

      // Se houver parcelas fracionadas registradas, o valor líquido total do recibo deve bater perfeitamente com a soma das partes pagas
      const splits = Array.isArray(payslip.payment_splits) ? payslip.payment_splits : [];
      const splitsSum = splits.reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0);
      const isSplit = payslip.payment_method === 'FRACIONADO' || splits.length > 1;

      const finalNetPaid = (isSplit && splitsSum > 0) ? splitsSum : computedNet;

      // Tabela de Proventos e Descontos Resumida
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(30, 41, 59);
      pdf.text('DEMONSTRATIVO DA COMPOSIÇÃO SALARIAL', margin, currentY);
      currentY += 4;

      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 26, 2, 2, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 26, 2, 2, 'S');

      pdf.setFontSize(8);
      pdf.setTextColor(71, 85, 105);

      // Coluna Proventos
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Salário Base Mensal:`, margin + 4, currentY + 5);
      pdf.setFont('helvetica', 'bold');
      pdf.text(baseSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), margin + 50, currentY + 5);

      if (satAmount > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Sábados Trabalhados:`, margin + 4, currentY + 10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(satAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), margin + 50, currentY + 10);
      }

      if ((otWeekday + otWeekend) > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Horas Extras:`, margin + 4, currentY + 15);
        pdf.setFont('helvetica', 'bold');
        pdf.text((otWeekday + otWeekend).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), margin + 50, currentY + 15);
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text(`Total Proventos:`, margin + 4, currentY + 21);
      pdf.text(grossCalculated.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), margin + 50, currentY + 21);

      // Coluna Descontos
      const col2X = pageWidth / 2 + 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);

      if (advanceDeduction > 0) {
        pdf.text(`Adiantamento Quinzenal:`, col2X, currentY + 5);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(220, 38, 38);
        pdf.text(`- ${advanceDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, pageWidth - margin - 4, currentY + 5, { align: 'right' });
      }

      if (inssDeduction > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        pdf.text(`Desconto INSS:`, col2X, currentY + 10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(220, 38, 38);
        pdf.text(`- ${inssDeduction.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, pageWidth - margin - 4, currentY + 10, { align: 'right' });
      }

      if (diverseExpenses > 0) {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        pdf.text(`Vales / Diversos:`, col2X, currentY + 15);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(220, 38, 38);
        pdf.text(`- ${diverseExpenses.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, pageWidth - margin - 4, currentY + 15, { align: 'right' });
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(220, 38, 38);
      pdf.text(`Total Descontos:`, col2X, currentY + 21);
      pdf.text(`- ${totalDeductions.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, pageWidth - margin - 4, currentY + 21, { align: 'right' });

      currentY += 32;

      // === DISCRIMINAÇÃO DO VALOR LÍQUIDO E DA FORMA DE PAGAMENTO ===
      const netSalary = finalNetPaid;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(30, 41, 59);
      pdf.text('DISCRIMINAÇÃO DO VALOR E FORMA DE PAGAMENTO', margin, currentY);
      currentY += 4;

      if (isSplit && splits.length > 0) {
        // Tabela de Pagamento Fracionado
        pdf.setFillColor(238, 242, 255);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 7, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(67, 56, 202);
        pdf.text('PAGAMENTO FRACIONADO (MÚLTIPLAS FORMAS)', margin + 3, currentY + 5);
        currentY += 8;

        splits.forEach((split, index) => {
          pdf.setFillColor(index % 2 === 0 ? 255 : 249, 250, 251);
          pdf.rect(margin, currentY, pageWidth - 2 * margin, 7, 'F');
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(55, 65, 81);
          pdf.text(`Parte ${index + 1}: ${split.method || 'Outro'}`, margin + 4, currentY + 4.5);
          pdf.setFont('helvetica', 'bold');
          pdf.text(Number(split.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), pageWidth - margin - 4, currentY + 4.5, { align: 'right' });
          currentY += 7;
        });
      } else {
        // Pagamento Único
        pdf.setFillColor(240, 253, 244);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 14, 'F');
        pdf.setDrawColor(187, 247, 208);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 14, 'S');

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(22, 101, 52);
        pdf.text('MODALIDADE: PAGAMENTO ÚNICO (100%)', margin + 4, currentY + 5);

        pdf.setFont('helvetica', 'normal');
        pdf.text(`Forma: ${payslip.payment_method || 'TRANSFERÊNCIA'}`, margin + 4, currentY + 10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(netSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), pageWidth - margin - 4, currentY + 10, { align: 'right' });
        currentY += 16;
      }

      // Totalizador em Destaque
      currentY += 2;
      pdf.setFillColor(73, 148, 61);
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 11, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      pdf.text('VALOR LÍQUIDO TOTAL PAGO', margin + 4, currentY + 7);
      pdf.text(netSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }), pageWidth - margin - 4, currentY + 7, { align: 'right' });

      currentY += 18;
      pdf.setTextColor(0, 0, 0);

      // Data de liquidação e observações
      const paidDate = payslip.paid_at ? new Date(payslip.paid_at) : new Date();
      const paidDateStr = paidDate.toLocaleDateString('pt-BR');

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Data da Liquidação / Pagamento: ${paidDateStr}`, margin, currentY);
      currentY += 5;

      if (payslip.payment_notes) {
        pdf.text(`Observações do Pagamento: ${payslip.payment_notes}`, margin, currentY);
        currentY += 6;
      }

      currentY += 6;

      // === TERMO DE DECLARAÇÃO E QUITAÇÃO LEGAL ===
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 32, 2, 2, 'F');
      pdf.setDrawColor(203, 213, 225);
      pdf.roundedRect(margin, currentY, pageWidth - 2 * margin, 32, 2, 2, 'S');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(15, 23, 42);
      pdf.text('TERMO DE DECLARAÇÃO E QUITAÇÃO INTEGRAL', margin + 4, currentY + 6);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(51, 65, 85);

      const paymentMethodDesc = isSplit
        ? 'de forma FRACIONADA conforme discriminado nas parcelas acima'
        : `através de ${payslip.payment_method || 'transferência/depósito bancário'}`;

      const quitacaoText = `Declaro para os devidos fins que recebi da empresa ${companyName} a importância líquida de ${netSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}, paga ${paymentMethodDesc}, referente à liquidação e quitação total das verbas salariais da competência de ${monthName}/${payslip.ref_year}. Reconheço a exatidão dos valores recebidos e conferidos, dando plena, rasa e irrevogável quitação.`;

      const textLines = pdf.splitTextToSize(quitacaoText, pageWidth - 2 * margin - 8);
      pdf.text(textLines, margin + 4, currentY + 12);

      currentY += 44;

      // === DATA E CAMPOS DE ASSINATURA ===
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Inhapim - MG, ${paidDate.getDate()} de ${monthNames[paidDate.getMonth()]} de ${paidDate.getFullYear()}.`, margin, currentY);

      currentY += 24;

      const colWidth = (pageWidth - 2 * margin - 15) / 2;

      // Linha Assinatura Empresa
      pdf.setDrawColor(100, 100, 100);
      pdf.setLineWidth(0.4);
      pdf.line(margin, currentY, margin + colWidth, currentY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(companyName, margin + colWidth / 2, currentY + 5, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Empregador / Responsável Financeiro', margin + colWidth / 2, currentY + 9, { align: 'center' });

      // Linha Assinatura Colaborador
      pdf.setTextColor(0, 0, 0);
      pdf.line(margin + colWidth + 15, currentY, pageWidth - margin, currentY);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.text(payslip.full_name || 'Colaborador', margin + colWidth + 15 + colWidth / 2, currentY + 5, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Assinatura do Colaborador', margin + colWidth + 15 + colWidth / 2, currentY + 9, { align: 'center' });

      // Rodapé
      this.addPayslipFooter(pdf, pageWidth, pageHeight, margin);

      const fileName = `Recibo_Quitacao_${payslip.full_name.replace(/\s+/g, '_')}_${monthName}_${payslip.ref_year}.pdf`;
      pdf.save(fileName);
      console.log('✅ Via de Recibo de Pagamento gerada com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao gerar Recibo de Pagamento:', error);
      throw error;
    }
  }

  /**
   * Adiciona rodapé do holerite
   */
  addPayslipFooter(pdf, pageWidth, pageHeight, margin) {
    const totalPages = pdf.internal.getNumberOfPages();
    
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      
      // Linha separadora
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      
      // Texto do rodapé
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      
      const footerText = `Sistema Santiago - Holerite de Pagamento - Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`;
      pdf.text(footerText, margin, pageHeight - 10);
      
      // Número da página
      pdf.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
  }
}

export default new PDFService();
